import { useEffect, useCallback, useRef, forwardRef, useImperativeHandle, useState } from "react";
import { Container, Alert, Button, Card, Row, Col, ListGroup, Form } from "react-bootstrap";
import Plotly from "plotly.js-dist";

const validDensityTypes = ["histogram2d", "density"];

const ApproximationGraph = forwardRef(({
    measurement = {},
    graphData,
    showGraph,
    loading,
    error,
    onClose,
    onRetry,
    customization,
    getColorPalette,
    applyTheme,
    onDegreeChange,
    initialDegree = 2,
    auth,
}, ref) => {
    const plotDivRef = useRef(null);
    const [degree, setDegree] = useState(initialDegree);
    const [authError, setAuthError] = useState(null);

    const handleDegreeChange = (e) => {
        const newDegree = parseInt(e.target.value);
        setDegree(newDegree);
        if (onDegreeChange) {
            onDegreeChange(newDegree);
        }
    };

    // Check for auth.token
    useEffect(() => {
        if (!auth.token) {
            setAuthError("Authentication token missing");
        } else {
            setAuthError(null);
        }
    }, [auth.token]);

    const exportGraph = useCallback(
        (format) => {
            if (!measurement?.id || !plotDivRef.current) return;

            Plotly.downloadImage(plotDivRef.current, {
                format: format,
                width: 1200,
                height: 1000,
                filename: `approximation-graph-for-${measurement.filename}`,
            });
        }, [measurement?.id]
    );

    const exportData = useCallback(() => {
        if (!graphData || !measurement?.id) return;

        let csvContent = "data:text/csv;charset=utf-8,";
        csvContent += "Sensor,X Value,Y Value,Fit Value\n";

        Object.entries(graphData).forEach(([sensorKey, sensorData]) => {
            sensorData.data.x?.forEach((xVal, idx) => {
                const yVal = sensorData.data.y?.[idx];
                let fitVal = "";

                if (sensorData.coefficients?.length >= 2) {
                    if (sensorData.coefficients.length === 2) {
                        // Degree 1: y = m * x + c
                        const [m, c] = [...sensorData.coefficients].reverse();
                        fitVal = m * xVal + c;
                    } else {
                        // Degree 2+: Quadratic or higher
                        const [a, b, c] = [...sensorData.coefficients].reverse();
                        fitVal = a * xVal * xVal + b * xVal + c;
                    }
                }

                csvContent += `${sensorKey},${xVal},${yVal},${fitVal}\n`;
            });
        });

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `approximation-data-for-${measurement.filename}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [graphData, measurement?.id]);

    useImperativeHandle(ref, () => ({
        exportGraph,
        exportData
    }));

    useEffect(() => {
        if (graphData && showGraph && measurement?.id && plotDivRef.current) {
            const plotData = [];
            const colors = getColorPalette();
            const sensorKeys = Object.keys(graphData);

            const config = {
                responsive: true,
                displayModeBar: true,
                modeBarButtonsToAdd: [
                {
                    name: "Download as PNG",
                    icon: Plotly.Icons.camera,
                    click: () => exportGraph("png"),
                },
                {
                    name: "Download as SVG",
                    icon: Plotly.Icons.camera,
                    click: () => exportGraph("svg"),
                },
                ],
                scrollZoom: true,
            };

            // Global z min/max for density plot
            let globalZmin = Infinity;
            let globalZmax = -Infinity;

            if (customization.graphType === "density") {
                Object.values(graphData).forEach((sd) => {
                    const count = sd.data.x?.length ?? 0;
                    globalZmin = Math.min(globalZmin, 0);
                    globalZmax = Math.max(globalZmax, count);
                });
                if (!isFinite(globalZmin)) globalZmin = 0;
                if (!isFinite(globalZmax)) globalZmax = 1;
            }

            // Handle faceted scatter plots
            if (customization.graphType === "facet") {
                const rows = Math.ceil(sensorKeys.length / customization.facetColumns);
                const grid = { rows: rows, columns: customization.facetColumns, pattern: "independent" };

                sensorKeys.forEach((sensorKey, index) => {
                    const sensorData = graphData[sensorKey];

                    if (customization.showRawData) {
                        plotData.push({
                            x: sensorData.data.x ?? [],
                            y: sensorData.data.y ?? [],
                            mode: "markers",
                            type: "scatter",
                            name: `${sensorKey} Data`,
                            marker: {
                                color: colors[index % colors.length],
                                size: 8,
                                opacity: 0.7,
                                symbol: customization.markerStyle,
                            },
                            hoverinfo: customization.hoverInfo,
                            xaxis: `x${index + 1}`,
                            yaxis: `y${index + 1}`,
                            showlegend: false,
                        });
                    }

                    if (customization.showFitLines && sensorData.coefficients?.length >= 2) {
                        const xRange = sensorData.data.x ?? [];
                        const minX = Math.min(...xRange);
                        const maxX = Math.max(...xRange);
                        const xFit = Array.from(
                            { length: 100 },
                            (_, i) => minX + (i * (maxX - minX)) / 99
                        );

                        let yFit;
                        if (sensorData.coefficients.length === 2) {
                            // Degree 1: y = m * x + c
                            const [m, c] = [...sensorData.coefficients].reverse();
                            yFit = xFit.map((x) => m * x + c);
                        } else {
                            // Degree 2+: Quadratic or higher

                            const [a, b, c] = [...sensorData.coefficients].reverse();
                            yFit = xFit.map((x) => a * x * x + b * x + c);
                        }

                        plotData.push({
                            x: xFit,
                            y: yFit,
                            mode: "lines",
                            name: `${sensorKey} Fit`,
                            line: {
                                color: colors[index % colors.length],
                                width: 2,
                                dash: customization.lineStyle === "solid" ? null : customization.lineStyle
                            },
                            hoverinfo: customization.hoverInfo,
                            xaxis: `x${index + 1}`,
                            yaxis: `y${index + 1}`,
                            showlegend: false,
                        });
                    }
                });

                const layout = applyTheme(
                    {
                        title: `Faceted Approximation Graph for ${measurement.filename}`,
                        grid: grid,
                        showlegend: false,
                        margin: { t: 50, b: 80, l: 80, r: 50 },
                    },
                    customization.theme
                );

                sensorKeys.forEach((_, index) => {
                    layout[`xaxis${index + 1}`] = {
                        title: "Temperature",
                        showgrid: customization.showGrid,
                        gridcolor: customization.theme === "dark" ? "#444" : "#eee",
                    };
                    layout[`yaxis${index + 1}`] = {
                        title: "Error",
                        showgrid: customization.showGrid,
                        gridcolor: customization.theme === "dark" ? "#444" : "#eee",
                    };
                });

                Plotly.react(plotDivRef.current, plotData, layout, config);
                return;
            }

            // Handle density plots
            if (customization.graphType === "density") {
                const columns = customization.densityColumns;
                const rows = Math.ceil(sensorKeys.length / columns);

                const grid = {
                    rows: rows,
                    columns: columns,
                    pattern: "independent",
                };

                sensorKeys.forEach((sensorKey, index) => {
                    const sensorData = graphData[sensorKey];
                    const densityType = validDensityTypes.includes(customization.densityType) ? customization.densityType : "histogram2d";

                    plotData.push({
                        x: sensorData.data.x ?? [],
                        y: sensorData.data.y ?? [],
                        type: densityType,
                        name: `${sensorKey} Density`,
                        colorscale: getColorPalette(customization.colorPalette),
                        nbinsx: customization.densityBinSize || 20,
                        nbinsy: customization.densityBinSize || 20,
                        hovertemplate:
                            `<b>Sensor:</b> ${sensorKey}<br>` +
                            `<b>X:</b> %{x}<br>` +
                            `<b>Y:</b> %{y}<br>` +
                            `<b>Data Points:</b> %{z}<extra></extra>`,
                        showscale: index === 0, // Only show colorbar for first plot
                        zmin: customization.zmin ?? globalZmin,
                        zmax: customization.zmax ?? globalZmax,
                        colorbar: {
                            lenmode: "fraction",
                            len: 0.75,
                            thickness: 20,
                            ticks: "outside",
                            tickfont: { size: 12 },
                            tickformat: customization.colorbarFormat ?? ".2f",
                            title: { text: "Density", side: "right" },
                        },
                        xaxis: `x${index + 1}`,
                        yaxis: `y${index + 1}`,
                    });
                });

                const layout = applyTheme(
                    {
                        title: `Density Plots for ${measurement.filename}`,
                        grid: grid,
                        showlegend: false,
                        margin: { t: 50, b: 80, l: 80, r: 50 },
                    },
                    customization.theme
                );

                sensorKeys.forEach((_, index) => {
                    layout[`xaxis${index + 1}`] = {
                        title: index % columns === 0 ? "Temperature" : "", // Only label first column
                        showgrid: customization.showGrid,
                        gridcolor: customization.theme === "dark" ? "#444" : "#eee",
                    };
                    layout[`yaxis${index + 1}`] = {
                        title: index < columns ? "Error" : "", // Only label first row
                        showgrid: customization.showGrid,
                        gridcolor: customization.theme === "dark" ? "#444" : "#eee",
                    };
                });

                Plotly.react(plotDivRef.current, plotData, layout, config);
                return;
            }

            // Handle scatter and line graphs
            if (customization.graphType !== "density") {
                Object.entries(graphData).forEach(
                    ([sensorKey, sensorData], index) => {
                        if (customization.showRawData) {
                            const trace = {
                                x: sensorData.data.x ?? [],
                                y: sensorData.data.y ?? [],
                                mode:
                                    customization.graphType === "scatter"
                                        ? "markers"
                                        : customization.graphType === "line"
                                        ? "lines"
                                        : "markers",
                                type: customization.graphType === "scatter" ? customization.graphType : "",
                                name: `${sensorKey} Data`,
                                marker: {
                                    color: colors[index % colors.length],
                                    size: customization.graphType === "scatter" ? customization.bubbleSize : 8,
                                    opacity: 0.7,
                                    symbol: customization.markerStyle,
                                    line: {
                                        width: 1,
                                        color: customization.theme === "dark" ? "#222" : "#fff",
                                    },
                                },
                                hoverinfo: customization.hoverInfo,
                            };

                            if (customization.showTrendline && customization.graphType === "scatter") {
                                trace.mode = "markers+lines";
                                trace.line = {
                                    color: colors[index % colors.length],
                                    width: 1,
                                    dash: "dot"
                                };
                            }

                            plotData.push(trace);
                        }

                        if (customization.showFitLines && sensorData.coefficients?.length >= 2) {
                            const xRange = sensorData.data.x ?? [];
                            const minX = Math.min(...xRange);
                            const maxX = Math.max(...xRange);
                            const xFit = Array.from(
                                { length: 100 },
                                (_, i) => minX + (i * (maxX - minX)) / 99
                            );

                            let yFit;
                            if (sensorData.coefficients.length === 2) {
                                // Degree 1: y = m * x + c
                                const [m, c] = [...sensorData.coefficients].reverse();
                                yFit = xFit.map((x) => m * x + c);
                            } else {
                                // Degree 2+: Quadratic or higher
                                const [a, b, c] = [...sensorData.coefficients].reverse();
                                yFit = xFit.map((x) => a * x * x + b * x + c);
                            }

                            plotData.push({
                                x: xFit,
                                y: yFit,
                                mode: "lines",
                                name: `${sensorKey} Fit`,
                                line: {
                                    color: colors[index % colors.length],
                                    width: 2,
                                    dash: customization.lineStyle === "solid" ? null : customization.lineStyle
                                },
                                hoverinfo: customization.hoverInfo,
                            });
                        }
                    }
                );
            }

            let minX = Infinity;
            let maxX = -Infinity;

            Object.values(graphData).forEach((sensorData) => {
                const sensorMin = Math.min(...(sensorData.data.x ?? []));
                const sensorMax = Math.max(...(sensorData.data.x ?? []));
                minX = Math.min(minX, sensorMin);
                maxX = Math.max(maxX, sensorMax);
            });

            const roundedMin = Math.floor(minX);
            const roundedMax = Math.ceil(maxX);
            const offset = 2;
            const minXtoShow = roundedMin - offset;
            const maxXtoShow = roundedMax + offset;

            const tickStep = 1;
            const tickvals = [];
            for (let i = minXtoShow; i <= maxXtoShow; i += tickStep) {
                tickvals.push(i);
            }

            const layout = applyTheme(
                {
                    title: `Approximation Graph for ${measurement.filename}`,
                    xaxis: {
                        title: "Temperature",
                        showgrid: customization.showGrid,
                        gridcolor: customization.theme === "dark" ? "#444" : "#eee",
                        range: [minXtoShow, maxXtoShow],
                        tickvals: tickvals,
                        tickmode: "array",
                        rangemode: "normal",
                    },
                    yaxis: {
                        title: "Error",
                        showgrid: customization.showGrid,
                        gridcolor: customization.theme === "dark" ? "#444" : "#eee",
                        rangemode: "tozero",
                    },
                    showlegend: customization.showLegend,
                    margin: { t: 50, b: 80, l: 80, r: 50 },
                    hovermode: "closest",
                    legend: { orientation: "h", y: -0.2 },
                    annotations: [],
                },
                customization.theme
            );

            try {
                Plotly.react(plotDivRef.current, plotData, layout, config);
            } catch (plotError) {
                alert("Failed to render graph. Please try again.", plotError);
            }
        }
    }, [graphData, showGraph, measurement?.id, customization, degree, exportGraph, getColorPalette, applyTheme]);

    if (!measurement?.id) {
        return (
            <Container className="py-4">
                <Alert variant="warning">Please select a valid measurement to view the graph.</Alert>
                <Button variant="secondary" onClick={onClose}> Close </Button>
            </Container>
        );
    }

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3>{measurement?.filename || "File Content"}</h3>

                <div className="d-flex align-items-center gap-3">
                    <Form.Group controlId="degreeSelect" className="mb-0">
                        <Form.Label className="me-2 mb-0">Polynomial Degree</Form.Label>
                        <Form.Select
                            size="sm"
                            value={degree}
                            onChange={handleDegreeChange}
                            style={{ width: '80px' }}
                        >
                            <option value="1">1</option>
                            <option value="2">2</option>
                            <option value="3">3</option>
                            <option value="4">4</option>
                        </Form.Select>
                    </Form.Group>
                </div>
            </div>

            {error && (
                <Alert variant="danger" className="mb-4">
                    {error}
                    <Button variant="outline-danger" size="sm" onClick={onRetry} className="ms-3"> Retry </Button>
                </Alert>
            )}

            {!loading && !error && (
                <>
                    <div id="plotly-div" ref={plotDivRef} style={{ minHeight: "900px", width: "100%", overflowY: "auto" }} />

                    {/* Display coefficients for each sensor */}
                    <Row className="mt-1 gx-3 gy-3">
                        {graphData && Object.entries(graphData).map(([sensorId, sensorData]) => {
                            const coeffs = sensorData.coefficients ?? [];
                            const letters = ["a", "b", "c", "d", "e"];
                            const reversedCoeffs = coeffs.slice().reverse();

                            return (
                                <Col key={sensorId} xs={12} md={4} lg={3}>
                                    <Card className="h-100 shadow-sm mt-3">
                                        <Card.Body>
                                            <Card.Title className="text-primary fs-6">{sensorId}</Card.Title>
                                            <ListGroup variant="flush">
                                                {reversedCoeffs.map((coef, idx) => (
                                                    <ListGroup.Item key={idx} className="text-muted small">
                                                        {letters[idx] || `coef${idx}`} = {coef}
                                                    </ListGroup.Item>
                                                ))}
                                            </ListGroup>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>

                    <div className="text-muted small mt-5">
                        Showing data for {Object.keys(graphData).length} sensors |
                        Data points: {Object.values(graphData).reduce(
                            (sum, sensor) => sum + (sensor.data.x?.length ?? 0), 0
                        )} |{" "}
                    </div>
                </>
            )}
        </>
    );
});

export default ApproximationGraph;